// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { getProductListDataViewProperty } from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@steiner/App/Store/Reducers";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => {
    // <XNG-Changes>
    const pagination = getProductListDataViewProperty(state, "pagination");
    return {
        view: state.pages.productList.view,
        // XNG-Changes
        hasProducts: pagination?.totalItemCount,
        isTabletScreen: state.xCommon.isTabletScreen,
    };
};

// XNG-Changes: add mapDispatchToProps return type
type Props = ReturnType<typeof mapStateToProps> & WidgetProps;

export interface ProductListControlsContainerStyles {
    container?: GridContainerProps;
    countGridItem?: GridItemProps;
    countTableViewGridItem?: GridItemProps;
    sortGridItem?: GridItemProps;
    sortTableViewGridItem?: GridItemProps;
    filterGridItem?: GridItemProps;
    filterTableViewGridItem?: GridItemProps;
    viewSelectGridItem?: GridItemProps;
    viewSelectTableViewGridItem?: GridItemProps;
    // XNG-Changes
    leftColumn?: InjectableCss;
    rightColumn?: InjectableCss;
    filterLink?: LinkPresentationProps;
}

export const controlsContainerStyles: ProductListControlsContainerStyles = {
    container: {
        gap: 0,
    },
    countGridItem: {
        width: [12, 12, 2, 5, 6],
    },
    countTableViewGridItem: {
        width: [12, 12, 1, 1, 1],
    },
    sortGridItem: {
        width: [12, 12, 5, 4, 4],
    },
    sortTableViewGridItem: {
        width: [12, 12, 5, 4, 3],
    },
    filterGridItem: {
        width: 0,
    },
    filterTableViewGridItem: {
        width: [0, 0, 2, 2, 2],
    },
    viewSelectGridItem: {
        width: [0, 0, 5, 3, 2],
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
    viewSelectTableViewGridItem: {
        width: [0, 0, 4, 5, 6],
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        display: none;
                    `,
                    css`
                        display: none;
                    `,
                    null,
                    null,
                    null,
                ])}
        `,
    },
};

const styles = controlsContainerStyles;

const ProductListControlsContainer: FC<Props> = ({ id, view, hasProducts, isTabletScreen }) => {
    //   XNG-Changes: dont show this widget when search has no product results (to match classic)
    if (!hasProducts) {
        return null;
    }

    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper {...styles.leftColumn}>
                <StyledWrapper className="count">
                    <Zone contentId={id} zoneName="Content00" />
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.rightColumn}>
                <StyledWrapper className="sortBy">
                    <Zone contentId={id} zoneName="Content03" />
                </StyledWrapper>
                <StyledWrapper className="perPage">
                    <Zone contentId={id} zoneName="Content04" />
                </StyledWrapper>
                <StyledWrapper className="view">
                    <Zone contentId={id} zoneName="Content01" />
                </StyledWrapper>
                <StyledWrapper className="pagination-page-number">
                    <Zone contentId={id} zoneName="Content02" />
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(ProductListControlsContainer),
    definition: {
        group: "Product List",
        displayName: "List Controls Container",
        allowedContexts: ["ProductListPage"],
    },
};

export default widgetModule;
